import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import { EmojiEventsOutlined as EmojiEventsOutlinedIcon, SaveOutlined as SaveOutlinedIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosConfig';
import PageContainer from '../common/PageContainer';
import PageHeader from '../common/PageHeader';

const TARGET_OPTIONS = [
  { label: 'Student Achievement', value: 'student' },
  { label: 'Teacher Achievement', value: 'teacher' },
];

const resolveStudentOptions = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const AchievementEntryPage = () => {
  const navigate = useNavigate();
  const [targetType, setTargetType] = useState('student');
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    dateAwarded: new Date().toISOString().slice(0, 10),
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadOptions = useCallback(async () => {
    setLoading(true);
    try {
      const [studentResponse, teacherResponse] = await Promise.all([
        axiosInstance.get('/api/students', { params: { pageNo: 1, pageSize: 300, search: '' } }),
        axiosInstance.get('/api/employees', { params: { role: 'TEACHER' } }),
      ]);

      setStudents(resolveStudentOptions(studentResponse?.data));
      setTeachers(Array.isArray(teacherResponse?.data) ? teacherResponse.data : []);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || error?.response?.data?.error || 'Failed to load students and teachers.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const currentOptions = useMemo(
    () => (targetType === 'teacher' ? teachers : students),
    [targetType, teachers, students]
  );

  const getOptionLabel = useCallback((option) => {
    if (!option) return '';
    if (targetType === 'teacher') {
      const fullName = `${option.firstName || ''} ${option.lastName || ''}`.trim();
      return `${fullName || 'Teacher'}${option.employeeCode ? ` • ${option.employeeCode}` : ''}`;
    }
    const fullName = `${option.firstName || ''} ${option.lastName || ''}`.trim();
    return `${fullName || 'Student'}${option.admissionNumber ? ` • ${option.admissionNumber}` : ''}`;
  }, [targetType]);

  const handleSave = async () => {
    if (!selectedTarget || !form.title.trim() || !form.dateAwarded) {
      setSnackbar({
        open: true,
        message: 'Select a target and fill title and award date.',
        severity: 'warning',
      });
      return;
    }

    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      dateAwarded: form.dateAwarded,
      ...(targetType === 'teacher'
        ? { teacherId: selectedTarget.employeeId }
        : { studentId: selectedTarget.studentId }),
    };

    setSaving(true);
    try {
      await axiosInstance.post(
        targetType === 'teacher' ? '/api/achievements/teachers' : '/api/achievements/students',
        payload
      );
      setSnackbar({
        open: true,
        message: 'Achievement added successfully.',
        severity: 'success',
      });
      setForm({
        title: '',
        description: '',
        dateAwarded: new Date().toISOString().slice(0, 10),
      });
      setSelectedTarget(null);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || error?.response?.data?.error || 'Failed to add achievement.',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Add Achievement"
        subtitle="Create student or teacher achievements from one simple admin form."
        actions={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => navigate(-1)}>
              Back
            </Button>
          </Stack>
        }
      />

      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          borderRadius: 4,
          borderColor: alpha('#0f172a', 0.08),
          boxShadow: '0 18px 45px -30px rgba(15,23,42,0.28)',
        }}
      >
        <Stack spacing={2.5}>
          <Box
            sx={{
              p: 2,
              borderRadius: 1.5,
              background: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.10)} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
              border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
            }}
          >
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha('#7c3aed', 0.12),
                  color: '#7c3aed',
                }}
              >
                <EmojiEventsOutlinedIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Achievement Entry
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Admin can add recognition for students and teachers. This route is meant for controlled achievement entry only.
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Achievement Type"
                value={targetType}
                onChange={(event) => {
                  setTargetType(event.target.value);
                  setSelectedTarget(null);
                }}
              >
                {TARGET_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={8}>
              <Autocomplete
                options={currentOptions}
                loading={loading}
                value={selectedTarget}
                onChange={(_, value) => setSelectedTarget(value)}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={(option, value) => {
                  if (targetType === 'teacher') {
                    return option.employeeId === value.employeeId;
                  }
                  return option.studentId === value.studentId;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={targetType === 'teacher' ? 'Select Teacher' : 'Select Student'}
                    placeholder={targetType === 'teacher' ? 'Search teacher' : 'Search student'}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Achievement Title"
                value={form.title}
                onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                placeholder="Best Attendance Award"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Award Date"
                type="date"
                value={form.dateAwarded}
                onChange={(event) => setForm((prev) => ({ ...prev, dateAwarded: event.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Description"
                value={form.description}
                onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                placeholder="Add a short note about the recognition, event, or achievement."
              />
            </Grid>
          </Grid>

          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              startIcon={<SaveOutlinedIcon />}
              onClick={handleSave}
              disabled={saving || loading}
            >
              {saving ? 'Saving...' : 'Save Achievement'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default AchievementEntryPage;
